import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/utils/cn';

const inputVariants = cva(
  'flex w-full rounded-lg border bg-white px-3 py-2 text-sm ring-offset-white file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-secondary-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-secondary-800 dark:ring-offset-secondary-900 dark:placeholder:text-secondary-400',
  {
    variants: {
      variant: {
        default: 'border-secondary-300 dark:border-secondary-600',
        error: 'border-danger-300 focus-visible:ring-danger-500 dark:border-danger-600',
        success: 'border-success-300 focus-visible:ring-success-500 dark:border-success-600',
      },
      size: {
        default: 'h-10',
        sm: 'h-8 text-xs',
        lg: 'h-12 text-base',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'>,
  VariantProps<typeof inputVariants> {
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  error?: string;
  helpText?: string;
  label?: string;
  required?: boolean;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, variant, size, leftIcon, rightIcon, error, helpText, label, required, id, ...props }, ref) => {
    const inputId = id ?? `input-${Math.random().toString(36).substr(2, 9)}`;
    const hasError = !!error;
    const effectiveVariant = hasError ? 'error' : variant;

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={inputId}
            className="mb-2 block text-sm font-medium text-secondary-900 dark:text-secondary-100"
          >
            {label}
            {required && <span className="ml-1 text-danger-500">*</span>}
          </label>
        )}
        <div className="relative">
          {leftIcon && (
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-secondary-400 dark:text-secondary-500">
              {leftIcon}
            </div>
          )}
          <input
            id={inputId}
            className={cn(
              inputVariants({ variant: effectiveVariant, size }),
              leftIcon && 'pl-10',
              rightIcon && 'pr-10',
              className
            )}
            ref={ref}
            {...props}
          />
          {rightIcon && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 text-secondary-400 dark:text-secondary-500">
              {rightIcon}
            </div>
          )}
        </div>
        {/* eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing */}
        {(error || helpText) && (
          <div className="mt-1 text-xs">
            {error && <p className="text-danger-600 dark:text-danger-400">{error}</p>}
            {!error && helpText && <p className="text-secondary-500 dark:text-secondary-400">{helpText}</p>}
          </div>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export { Input };